import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useWorkflow } from './WorkflowContext.tsx';
import styles from './Pads.module.css';
import useTheme from '../../hooks/useTheme.ts';
import { FiMapPin, FiHome, FiTruck } from 'react-icons/fi';
import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Organization {
  id: number;
  name: string;
}

interface PadsProps {
  onNext?: () => void;
  isViewer?: boolean;
}

/**
 * Simple module-level cache
 * Prevents refetching when component remounts (route/step changes) within the SPA session.
 */
let ORG_CACHE: Organization[] | null = null;
let GEO_CACHE: Record<string, string[]> | null = null;

const Pads: React.FC<PadsProps> = ({ onNext, isViewer = false }) => {
  const { theme } = useTheme();
  const { state, updatePadData } = useWorkflow();
  const { padData } = state;
  const isDark = theme === 'dark';

  // Local draft state for text inputs (prevents global update on every keystroke)
  const [padNameDraft, setPadNameDraft] = useState(padData.padName ?? '');

  // Keep drafts in sync if padData changes externally (switch pad/reset workflow/etc.)
  useEffect(() => setPadNameDraft(padData.padName ?? ''), [padData.padName]);

  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>(ORG_CACHE ?? []);
  const [geoMap, setGeoMap] = useState<Record<string, string[]>>(GEO_CACHE ?? {});
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(!ORG_CACHE);
  const [loadingGeo, setLoadingGeo] = useState<boolean>(!GEO_CACHE);

  // Generate fleet options 1-50
  const fleetOptions = useMemo(() => Array.from({ length: 50 }, (_, i) => i + 1), []);

  // States derived from geoMap (sorted)
  const states = useMemo(() => Object.keys(geoMap).sort(), [geoMap]);

  // Counties derived locally from selected state (sorted)
  const counties = useMemo(() => {
    if (!padData.state) return [];
    return (geoMap[padData.state] ?? []).slice().sort();
  }, [geoMap, padData.state]);

  // Fetch organizations ONCE (cached)
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (ORG_CACHE) {
        setOrganizations(ORG_CACHE);
        setLoadingOrgs(false);
        return;
      }

      setLoadingOrgs(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${backendUrl}/organizations/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        ORG_CACHE = response.data;
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Fetch states+counties ONCE (cached)
  useEffect(() => {
    const fetchGeo = async () => {
      if (GEO_CACHE) {
        setGeoMap(GEO_CACHE);
        setLoadingGeo(false);
        return;
      }

      setLoadingGeo(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${backendUrl}/geo/states-with-counties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data || {};
        GEO_CACHE = data;
        setGeoMap(data);
      } catch (error) {
        console.error('Error fetching geo (states-with-counties):', error);
        setGeoMap({});
      } finally {
        setLoadingGeo(false);
      }
    };

    fetchGeo();
  }, []);

  // Generic handler for low-frequency fields (selects, etc.)
  const handleInputChange = useCallback(
    (field: string, value: string | number) => {
      updatePadData({ [field]: value });
    },
    [updatePadData]
  );

  // Commit drafts only when user leaves the field
  const commitPadName = useCallback(() => {
    if ((padData.padName ?? '') !== padNameDraft) {
      updatePadData({ padName: padNameDraft });
    }
  }, [padData.padName, padNameDraft, updatePadData]);

  // Handle company selection - also store the org ID
  const handleCompanyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedName = e.target.value;
      const selectedOrg = organizations.find((org) => org.name === selectedName);
      updatePadData({
        company: selectedName,
        organizationId: selectedOrg?.id || 0
      });
    },
    [organizations, updatePadData]
  );

  // Handle state change - clear county when state changes
  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newState = e.target.value;
      updatePadData({
        state: newState,
        county: ''
      });
    },
    [updatePadData]
  );

  // Validation uses draft pad name (so it behaves well while typing)
  const isValid = padData.company && padNameDraft.trim().length > 0;

  return (
    <div className={`${styles.container} ${isDark ? styles.dark : ''}`}>
      <div className={styles.header}>
        <h2 className={styles.title}>Create New Pad</h2>
        <p className={styles.subtitle}>Enter the pad details to initialize your job configuration</p>
      </div>

      <div className={styles.formGrid}>
        {/* Company Info Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <FiHome className={styles.sectionIcon} />
            <span>Company Information</span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Company Name <span className={styles.required}>*</span>
              </label>
              <select
                className={styles.select}
                value={padData.company}
                onChange={handleCompanyChange}
                disabled={loadingOrgs || isViewer}
              >
                <option value="">{loadingOrgs ? 'Loading...' : 'Select Company...'}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.name}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Pad Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={padNameDraft}
                onChange={(e) => setPadNameDraft(e.target.value)}
                onBlur={commitPadName}
                placeholder="e.g., WTH 20-8 Pad 4"
                disabled={isViewer}
              />
            </div>
          </div>
        </div>

        {/* Location Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <FiMapPin className={styles.sectionIcon} />
            <span>Location</span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>State</label>
              <select
                className={styles.select}
                value={padData.state}
                onChange={handleStateChange}
                disabled={loadingGeo || isViewer}
              >
                <option value="">{loadingGeo ? 'Loading...' : 'Select State...'}</option>
                {states.map((stateName) => (
                  <option key={stateName} value={stateName}>
                    {stateName}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>County</label>
              <select
                className={styles.select}
                value={padData.county}
                onChange={(e) => handleInputChange('county', e.target.value)}
                disabled={!padData.state || loadingGeo || isViewer}
              >
                <option value="">
                  {loadingGeo ? 'Loading...' : !padData.state ? 'Select state first' : 'Select County...'}
                </option>
                {counties.map((countyName) => (
                  <option key={countyName} value={countyName}>
                    {countyName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Fleet Assignment Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <FiTruck className={styles.sectionIcon} />
            <span>Fleet Assignment</span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Assigned Fleet</label>
              <select
                className={styles.select}
                value={padData.fleet || ''}
                onChange={(e) => handleInputChange('fleet', parseInt(e.target.value, 10) || 0)}
              disabled={isViewer}
              >
                <option value="">Select Fleet...</option>
                {fleetOptions.map((fleetNum) => (
                  <option key={fleetNum} value={fleetNum}>
                    Fleet {fleetNum}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Hint */}
      {!isValid && (
        <div className={styles.validationHint}>
          <span className={styles.validationIcon}>ℹ️</span>
          <span>Please fill in Company Name and Pad Name to continue</span>
        </div>
      )}

      {/* Summary Card */}
      {isValid && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <span className={styles.summaryIcon}>✓</span>
            <span>Pad Configuration Ready</span>
          </div>
          <div className={styles.summaryDetails}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Company</span>
              <span className={styles.summaryValue}>{padData.company}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Pad</span>
              <span className={styles.summaryValue}>{padNameDraft}</span>
            </div>
            {(padData.state || padData.county) && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Location</span>
                <span className={styles.summaryValue}>
                  {padData.county ? `${padData.county}, ` : ''}
                  {padData.state}
                </span>
              </div>
            )}
            {padData.fleet > 0 && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Fleet</span>
                <span className={styles.summaryValue}>Fleet {padData.fleet}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pads;